import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { AnalyticsEvent, trackClick, useAnalytics } from '@bonusly/analytics';
import {
	AwardRibbonOutline,
	Box,
	Flex,
	HomeOutline,
	PlantOutline,
	ShoppingCartOutline,
	Show,
	useBreakpointValue,
	UserGroupOutline,
} from '@bonusly/design-system';
import { Permission, ProtectedContent, ProtectedContentWithPermissionOrFeatureFlag, useFeatureFlag, useIdentity, usePermission } from '@bonusly/who-am-i';

import { Reveal } from '../../../../components/display/reveal';
import { SeasonalToggle } from '../../../../components/seasonal';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';
import { useUserProfile } from '../../hooks/useUserProfile';
// TODO - Refactor to use helpers from from @bonusly/utilities
import { Variant } from '../FeatureFlag';
import { AnalyticsMenu } from '../Menu/AnalyticsMenu';
import { SearchModal } from '../SearchModal';
import { CompanyLogo } from './CompanyLogo';
import { InviteUsers } from './invite-users';
import { MonarchLink } from './MonarchLink';
import { NavbarShell } from './NavbarShell';
import { NavCircle } from './NavCircle';
import { NavLink } from './NavLink';
import { NavMenu } from './NavMenu';
import { NavNotificationMenu } from './NavNotificationMenu';

export const Navbar = () => {
	// TODO: Remove useUserProfile and fetch user from '@bonusly/who-am-i' package
	const { user } = useUserProfile();
	const { isUserIdentified } = useIdentity();
	const { company } = useCompanyProfile();
	const { t } = useTranslation('navigation');
	const { track, trackEvent } = useAnalytics();
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const location = useLocation();

	const displayFor = useBreakpointValue({
		base: 'mobile',
		xs: 'desktop',
	});

	const canAccessAwards = usePermission(Permission.CanAccessIncentives);
	const canAccessTeamHub = usePermission(Permission.CanAccessTeamHub);
	const homepageRedesignEnabled = useFeatureFlag('homepage-redesign');

	// Hide top nav analytics when homepage-redesign is enabled for global admins (analytics moved to sidebar)
	const showTopNavAnalytics = !(homepageRedesignEnabled && user?.global_admin);

	const isLoaded = !!user && isUserIdentified && !!company;

	return (
		<NavbarShell logo={<CompanyLogo />}>
			{isLoaded && (
				<Flex
					alignItems="center"
					as="ul"
					columnGap={1}
					display={{ base: 'none', xs: 'flex' }}
					flexBasis={0}
					flexGrow={1}
					flexDir="row"
					id="primary-nav-items"
					justifyContent="center"
					listStyleType="none"
				>
					<NavLink icon={<HomeOutline />} to="/">
						{t('links.home')}
					</NavLink>

					<ProtectedContentWithPermissionOrFeatureFlag
						flag={[Variant.OneOnOnesEarlyAccess, Variant.OneOnOnesLimitedRelease]}
						permissions={Permission.CanAccessOneOnOnes}
					>
						<MonarchLink icon={<PlantOutline />} to="/growth">
							{t('links.growth')}
						</MonarchLink>
					</ProtectedContentWithPermissionOrFeatureFlag>

					<Reveal if={canAccessTeamHub}>
						<Box onClick={() => trackEvent(AnalyticsEvent.TopNavbar.TeamHubNavigation, { pathway: 'menu' })}>
							<MonarchLink icon={<UserGroupOutline />} to="/team/your-team" isActive={location.pathname.startsWith('/team')}>
								{t('links.team')}
							</MonarchLink>
						</Box>
					</Reveal>

					<MonarchLink
						icon={<ShoppingCartOutline />}
						to="/rewards"
						id="navbar-rewards"
						{...trackClick(AnalyticsEvent.Rewards.ClickRewardsLink, {
							source: 'nav',
							userBalance: user.earning_balance,
						})}
					>
						{t('links.rewards')}
					</MonarchLink>

					{canAccessAwards && (
						<Box onClick={() => track(AnalyticsEvent.TopNavbar.IncentivesNavigation, { pathway: 'menu' })}>
							<MonarchLink icon={<AwardRibbonOutline />} to="/incentives" id="navbar-awards">
								{t('links.incentives')}
							</MonarchLink>
						</Box>
					)}

					{user.show_analytics && showTopNavAnalytics && (
						<ProtectedContent permissions={Permission.CanAccessAnalytics}>
							<AnalyticsMenu />
						</ProtectedContent>
					)}
				</Flex>
			)}

			<Flex flexBasis={0} flexGrow={1} justifyContent="flex-end" listStyleType="none">
				<Flex id="primary-nav-menu" columnGap={3} alignItems="center">
					{isLoaded ? (
						<>
							<Show above="xs">
								<SeasonalToggle />
							</Show>
							<InviteUsers />
							{!isMobile && <SearchModal />}
							<Show above="xs">
								<NavNotificationMenu user={user} />
							</Show>
							<NavMenu displayFor={displayFor} />
						</>
					) : (
						<>
							<NavCircle />
							<Show above="xs">
								<NavCircle />
							</Show>
							<NavCircle />
						</>
					)}
				</Flex>
			</Flex>
		</NavbarShell>
	);
};
